import { Button, Kind, Size } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import { alert } from 'actions/flash'
import imgOloPromoSetup from 'assets/images/promo_codes/olo_promo_setup.png'
import Badge from 'components/Badge'
import Copy from 'components/Icons/Copy'
import palette from 'constants/palette'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import { buildTranslate } from 'locales'
import React from 'react'
import { Container } from 'react-bootstrap-five'
import Helmet from 'react-helmet'
import { useDispatch } from 'react-redux'

const t = buildTranslate('promo_codes')
const tPlaceholder = buildTranslate('promo_codes.olo_placeholder')

const SUPPORT_EMAIL = 'success@thanx.com'
const NUMBER_BADGE_SIZE = 28
const CONNECTOR_WIDTH = 2
const CONNECTOR_MIN_HEIGHT = 16
const STEP_VERTICAL_GAP = 24
const COPY_ICON_SIZE = 20
const COPY_SUCCESS_TIMEOUT_SECONDS = 5

const OloPlaceholder = () => {
  const [css] = useStyletron()
  const dispatch = useDispatch()
  const merchant = useCurrentMerchant()
  const clientId = merchant?.hashid ?? ''
  // Without a Client ID the note is useless to the Olo rep, so copy is disabled
  // and the ID is omitted from the visible note. In practice the merchant is
  // always present here (PromoCodes/index.tsx guards on it), so this is
  // defensive.
  const hasClientId = clientId.trim().length > 0

  // The note is copied verbatim and handed to the Olo rep, so the merchant
  // Client ID is interpolated into a single source-of-truth string that both
  // the visible note and the clipboard payload are built from.
  const notePrefix = tPlaceholder('note_prefix')
  const noteSuffix = tPlaceholder('note_suffix')
  const noteText = hasClientId
    ? `${notePrefix} ${clientId}. ${noteSuffix}`
    : `${notePrefix} ${noteSuffix}`

  const handleCopy = async () => {
    // navigator.clipboard.writeText is only available in secure contexts (https)
    // and can reject (permission denied / sandboxed), so guard and surface a
    // failure toast instead of leaving an unhandled rejection.
    if (!navigator.clipboard?.writeText || !hasClientId) return
    try {
      await navigator.clipboard.writeText(noteText)
      dispatch(
        alert({
          key: 'success',
          message: tPlaceholder('copy_success'),
          timeout: COPY_SUCCESS_TIMEOUT_SECONDS,
        })
      )
    } catch {
      dispatch(alert({ key: 'danger', message: tPlaceholder('copy_error') }))
    }
  }

  const steps: { title: string; body: React.ReactNode }[] = [
    {
      title: tPlaceholder('step_1_title'),
      body: tPlaceholder('step_1_body'),
    },
    {
      title: tPlaceholder('step_2_title'),
      body: (
        <>
          {tPlaceholder('step_2_body_prefix')}{' '}
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className={css({ color: palette.belizeHole })}
          >
            {SUPPORT_EMAIL}
          </a>{' '}
          {tPlaceholder('step_2_body_suffix')}
        </>
      ),
    },
    {
      title: tPlaceholder('step_3_title'),
      body: tPlaceholder('step_3_body'),
    },
  ]

  return (
    <main data-testid="olo-placeholder">
      <Helmet>
        <title>{tPlaceholder('page_title')}</title>
      </Helmet>
      <Container>
        <div className="d-flex py-xl justify-content-between align-items-center">
          <Text.Header2 className="m-0">{t('landing.title')}</Text.Header2>
        </div>
      </Container>
      <div className="border-bottom-1 grey-20-border" />
      <div
        className={`grey-05-bkg ${css({
          paddingTop: '48px',
          paddingBottom: '48px',
        })}`}
      >
        <Container>
          <Badge
            value={tPlaceholder('badge')}
            className={css({
              backgroundColor: palette.spearmint10,
              color: palette.spearmint50,
            })}
          />
          <Text.Header3 className="mt-m mb-xs" color="grey90">
            {tPlaceholder('title')}
          </Text.Header3>
          <Text.BodyText3 color="grey70" tag="p" className="mb-l">
            {tPlaceholder('body')}
          </Text.BodyText3>

          {/* Ordered list so screen readers announce the 1-2-3 sequence
              (WCAG 1.3.1); the visual number badges are decorative and hidden. */}
          <ol
            // list-style: none drops the list role in Safari VoiceOver, so
            // restore it explicitly to preserve the WCAG 1.3.1 semantics above.
            role="list"
            className={css({
              display: 'flex',
              flexDirection: 'column',
              listStyle: 'none',
              padding: 0,
              margin: 0,
            })}
          >
            {steps.map((step, index) => {
              const isLastStep = index === steps.length - 1
              return (
                <li
                  key={step.title}
                  className={css({ display: 'flex', gap: '8px' })}
                >
                  <div
                    aria-hidden="true"
                    className={css({
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      alignSelf: 'stretch',
                    })}
                  >
                    <div
                      className={css({
                        flexShrink: 0,
                        width: `${NUMBER_BADGE_SIZE}px`,
                        height: `${NUMBER_BADGE_SIZE}px`,
                        borderRadius: '50%',
                        backgroundColor: palette.belizeHole10,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      })}
                    >
                      <Text.BodyText4 bold color="belizeHole" tag="span">
                        {index + 1}
                      </Text.BodyText4>
                    </div>
                    {!isLastStep && (
                      <div
                        className={css({
                          width: `${CONNECTOR_WIDTH}px`,
                          flex: '1 1 auto',
                          minHeight: `${CONNECTOR_MIN_HEIGHT}px`,
                          marginTop: '4px',
                          marginBottom: '4px',
                          backgroundColor: palette.grey30,
                        })}
                      />
                    )}
                  </div>
                  <div
                    className={css({
                      paddingBottom: isLastStep
                        ? '0'
                        : `${STEP_VERTICAL_GAP}px`,
                    })}
                  >
                    <Text.BodyText4 bold color="grey90" tag="div">
                      {step.title}
                    </Text.BodyText4>
                    <Text.BodyText4 color="grey70" tag="div">
                      {step.body}
                    </Text.BodyText4>
                  </div>
                </li>
              )
            })}
          </ol>

          <div
            className={css({
              marginTop: '24px',
              border: `1px solid ${palette.grey30}`,
              borderRadius: '8px',
              backgroundColor: palette.white,
              overflow: 'hidden',
            })}
          >
            <div
              className={css({
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px',
              })}
            >
              <Text.Header4 bold color="grey90" className="m-0">
                {tPlaceholder('note_title')}
              </Text.Header4>
              <Button
                kind={Kind.SECONDARY}
                size={Size.SMALL}
                onClick={handleCopy}
                disabled={!hasClientId}
                startEnhancer={() => <Copy size={COPY_ICON_SIZE} />}
              >
                {tPlaceholder('copy')}
              </Button>
            </div>
            <div
              className={`grey-05-bkg ${css({
                padding: '24px',
                borderTop: `1px solid ${palette.grey30}`,
              })}`}
            >
              <Text.BodyText3
                color="grey90"
                tag="p"
                className={`m-0 ${css({
                  // Monospace mirrors the copy-paste "code block" treatment in the
                  // design — it signals the note is a verbatim string to hand off.
                  fontFamily: 'monospace',
                })}`}
              >
                {hasClientId ? (
                  <>
                    {notePrefix}{' '}
                    {/* Bold + color so the Client ID is distinguishable without
                        relying on color alone (WCAG 1.4.1). */}
                    <span
                      className={css({
                        color: palette.belizeHole,
                        fontWeight: 'bold',
                      })}
                    >
                      {clientId}
                    </span>
                    {'. '}
                    {noteSuffix}
                  </>
                ) : (
                  `${notePrefix} ${noteSuffix}`
                )}
              </Text.BodyText3>
            </div>
          </div>

          <div
            className={css({
              display: 'flex',
              alignItems: 'center',
              gap: '32px',
              width: '100%',
              marginTop: '48px',
            })}
          >
            <div className={css({ flex: '1 1 0', minWidth: 0 })}>
              <Text.Header3 className="mt-0 mb-m" color="grey90">
                {tPlaceholder('feature_title')}
              </Text.Header3>
              <Text.BodyText3 color="grey70">
                {tPlaceholder('feature_body')}
              </Text.BodyText3>
            </div>
            <img
              src={imgOloPromoSetup}
              alt=""
              aria-hidden="true"
              className={css({ maxWidth: '60%', height: 'auto' })}
            />
          </div>
        </Container>
      </div>
    </main>
  )
}

export default OloPlaceholder
